import { Alpine } from '../../core/alpine-core.js';
import { apiFetch } from '../api.js';
import { stripLeagueFromAdminPath } from '../league-context.js';
import { gameTemplate_4S_3Dbl_1Beer_beer1001, MATCH_PROGRAM_PRESETS } from '../league-match-program-presets.js';
import {
  buildFixtureSchedulePreview,
  dateIsoOnly,
  FIXTURE_CADENCE_OPTIONS,
  normalizeMatchStartTime,
  WEEKDAYS,
} from '../league-schedule-cadence.js';
import {
  downloadBlob,
  parseImportDocument,
  pickTextFile,
  sanitizeExportBasename,
  textExportWithJsonSection,
  wrapExportDocument,
} from '../league-file-io.js';
import {
  buildLeagueRulesSummaryLines,
  leagueRulesExportPayload,
} from '../league-rules-summary.js';

const LEAGUE_RULES_EXPORT_KIND = 'dartsos.league-rules.v1';

const DEFAULT_TEMPLATE = () => gameTemplate_4S_3Dbl_1Beer_beer1001();
const clampBestOfLegs = (value) => Math.min(10, Math.max(1, Number(value) || 3));
const FORMAT_PRESETS = new Set(['501', '601', '1001', 'cricket']);

function normalizeStartingScore(value, fallback = 501) {
  const s = String(value ?? '').trim().toLowerCase();
  if (s === 'cricket') return 'cricket';
  if (FORMAT_PRESETS.has(s)) return s;
  const n = Number(value);
  if (Number.isFinite(n) && n >= 2 && n <= 9999) return String(Math.round(n));
  return normalizeStartingScore(fallback, 501);
}

function normalizeMaxDarts(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.min(180, Math.max(1, Math.round(n)));
}

function attachFormatUiFields(row) {
  const f = String(row.format || '501').toLowerCase();
  if (FORMAT_PRESETS.has(f)) {
    row.formatPreset = f;
    row.formatCustom = '';
  } else {
    row.formatPreset = 'custom';
    row.formatCustom = Number.isFinite(Number(f)) ? Number(f) : row.format || '';
  }
}

function enrichGameTemplateRow(g, defaults = {}) {
  const row = {
    order: Number(g.order) || 1,
    type: g.type || 'singles',
    format: g.format || defaults.defaultStartingScore || '501',
    bestOfLegs: clampBestOfLegs(g.bestOfLegs),
    label: g.label || '',
    checkout: g.checkout || '',
    maxDartsPerLeg:
      g.maxDartsPerLeg != null && g.maxDartsPerLeg !== ''
        ? normalizeMaxDarts(g.maxDartsPerLeg)
        : normalizeMaxDarts(defaults.defaultMaxDartsPerLeg),
  };
  attachFormatUiFields(row);
  return row;
}

function resolveRowFormat(row, defaultStartingScore) {
  if (row.formatPreset === 'custom') {
    return normalizeStartingScore(row.formatCustom, defaultStartingScore);
  }
  return normalizeStartingScore(row.formatPreset || defaultStartingScore, defaultStartingScore);
}

Alpine.data('leagueRulesEdit', () => ({
  loading: true,
  saving: false,
  isEditing: false,
  error: '',
  rulesSummaryOpen: false,
  rulesImporting: false,
  deleteOpen: false,
  deleteBusy: false,
  usageLoading: false,
  linkedLeagues: [],

  fixtureCadenceOptions: FIXTURE_CADENCE_OPTIONS,
  weekdayOptions: WEEKDAYS,
  schedulePreviewFrom: dateIsoOnly(new Date()),

  form: {
    _id: null,
    displayName: '',
    notes: '',
    defaultCheckout: 'double-out',
    defaultStartingScore: 501,
    defaultMaxDartsPerLeg: null,
    matchStartTime: '19:30',
    fixtureCadence: 'weekly',
    fixtureWeekday: 'thu',
    fixtureWeekday2: 'tue',
    gameTemplate: DEFAULT_TEMPLATE(),
  },

  isAccountSettingsEmbed() {
    const p = stripLeagueFromAdminPath(globalThis.location.pathname || '');
    return p === '/admin/account-settings' || p.startsWith('/admin/account-settings/');
  },

  readRouteId() {
    const url = new URL(globalThis.location.href);
    const path = url.hash && url.hash.startsWith('#/') ? url.hash.slice(1) : url.pathname;
    const parts = String(path).split('/').filter(Boolean);
    const idx = parts.indexOf('league-rules');
    if (idx >= 0 && parts[idx + 1] === 'new') return null;
    if (idx >= 0 && parts[idx + 1] && parts[idx + 2] === 'edit') return decodeURIComponent(parts[idx + 1]);
    if (this.isAccountSettingsEmbed()) {
      const st = this.$store.accountLeagueRules;
      if (st?.view === 'edit' && st?.editId) return String(st.editId);
    }
    return null;
  },

  isNewRoute() {
    const url = new URL(globalThis.location.href);
    const path = url.hash && url.hash.startsWith('#/') ? url.hash.slice(1) : url.pathname;
    const parts = String(path).split('/').filter(Boolean);
    const idx = parts.indexOf('league-rules');
    if (idx >= 0 && parts[idx + 1] === 'new') return true;
    return this.isAccountSettingsEmbed() && this.$store.accountLeagueRules?.view === 'new';
  },

  rowDefaults() {
    return {
      defaultStartingScore: normalizeStartingScore(this.form.defaultStartingScore, 501),
      defaultMaxDartsPerLeg: this.form.defaultMaxDartsPerLeg,
    };
  },

  applyDefaultsToTemplate(rows) {
    const defaults = this.rowDefaults();
    return rows.map((g) => enrichGameTemplateRow(g, defaults));
  },

  onRowFormatPresetChange(row) {
    if (!row) return;
    if (row.formatPreset !== 'custom') {
      row.format = row.formatPreset;
      row.formatCustom = '';
      return;
    }
    row.formatCustom = row.formatCustom || Number(this.form.defaultStartingScore) || 501;
    row.format = normalizeStartingScore(row.formatCustom, this.form.defaultStartingScore);
  },

  syncRowFormatFromCustom(row) {
    if (!row || row.formatPreset !== 'custom') return;
    row.format = normalizeStartingScore(row.formatCustom, this.form.defaultStartingScore);
  },

  async init() {
    const id = this.readRouteId();
    const isNew = this.isNewRoute();
    this.isEditing = Boolean(id) && !isNew;
    if (this.isEditing) {
      await this.load(id);
    } else {
      this.form.gameTemplate = this.applyDefaultsToTemplate(DEFAULT_TEMPLATE());
    }
    this.loading = false;
  },

  backToList() {
    if (this.isAccountSettingsEmbed()) {
      this.$store.accountLeagueRules.openList({ bump: true });
      return;
    }
    const router = globalThis.PineconeRouter;
    const next = '/admin/league-rules';
    if (router?.navigate) router.navigate(next);
    else globalThis.location.assign(next);
  },

  async load(id) {
    this.loading = true;
    this.error = '';
    try {
      const res = await apiFetch(`/league-rules/${encodeURIComponent(id)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || data?.error || 'Not found');
      const r = data?.rules || (data?.ok !== false && data?._id ? data : null);
      if (!r) throw new Error(data?.message || 'Not found');
      const defaultStartingScore = normalizeStartingScore(r.defaultStartingScore, 501);
      const defaultMaxDartsPerLeg = normalizeMaxDarts(r.defaultMaxDartsPerLeg);
      this.linkedLeagues = Array.isArray(data?.linkedLeagues) ? data.linkedLeagues : [];
      this.form = {
        _id: r._id,
        displayName: r.displayName || '',
        notes: r.notes || '',
        defaultCheckout: r.defaultCheckout || 'double-out',
        defaultStartingScore: defaultStartingScore === 'cricket' ? 501 : Number(defaultStartingScore) || 501,
        defaultMaxDartsPerLeg,
        matchStartTime: normalizeMatchStartTime(r.matchStartTime),
        fixtureCadence: r.fixtureCadence || 'weekly',
        fixtureWeekday: r.fixtureWeekday || 'thu',
        fixtureWeekday2: r.fixtureWeekday2 || 'tue',
        gameTemplate:
          Array.isArray(r.gameTemplate) && r.gameTemplate.length
            ? r.gameTemplate.map((g) =>
                enrichGameTemplateRow(g, { defaultStartingScore, defaultMaxDartsPerLeg }),
              )
            : this.applyDefaultsToTemplate(DEFAULT_TEMPLATE()),
      };
    } catch (e) {
      this.error = e.message || 'Failed to load';
    } finally {
      this.loading = false;
    }
  },

  renumberOrders() {
    this.form.gameTemplate.forEach((row, i) => {
      row.order = i + 1;
    });
  },

  matchProgramPresets: MATCH_PROGRAM_PRESETS,

  cadenceNeedsSecondWeekday() {
    return this.form.fixtureCadence === 'twice_weekly';
  },

  selectedCadenceHint() {
    return this.fixtureCadenceOptions.find((c) => c.id === this.form.fixtureCadence)?.hint || '';
  },

  fixtureSchedulePreview() {
    return buildFixtureSchedulePreview(10, {
      cadence: this.form.fixtureCadence,
      weekday1: this.form.fixtureWeekday,
      weekday2: this.form.fixtureWeekday2,
      startDate: this.schedulePreviewFrom,
      matchStartTime: this.form.matchStartTime,
    });
  },

  applyMatchProgramPreset(presetId) {
    const preset = MATCH_PROGRAM_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    this.form.gameTemplate = preset.gameTemplate().map((g) =>
      enrichGameTemplateRow(
        {
          ...g,
          bestOfLegs: clampBestOfLegs(g.bestOfLegs),
          checkout: g.checkout || '',
          maxDartsPerLeg: g.maxDartsPerLeg ?? this.form.defaultMaxDartsPerLeg ?? null,
        },
        this.rowDefaults(),
      ),
    );
    this.renumberOrders();
  },

  addGameRow() {
    const format = normalizeStartingScore(this.form.defaultStartingScore, 501);
    this.form.gameTemplate.push(
      enrichGameTemplateRow(
        {
          order: this.form.gameTemplate.length + 1,
          type: 'singles',
          format,
          bestOfLegs: 3,
          label: '',
          checkout: '',
          maxDartsPerLeg: this.form.defaultMaxDartsPerLeg ?? null,
        },
        this.rowDefaults(),
      ),
    );
    this.renumberOrders();
  },

  normalizeBestOfLegsRow(row) {
    if (!row || typeof row !== 'object') return;
    row.bestOfLegs = clampBestOfLegs(row.bestOfLegs);
  },

  removeGameRow(index) {
    this.form.gameTemplate.splice(index, 1);
    this.renumberOrders();
  },

  rulesSummaryLines() {
    return buildLeagueRulesSummaryLines(this.form);
  },

  openRulesSummaryDialog() {
    this.rulesSummaryOpen = true;
  },

  closeRulesSummaryDialog() {
    this.rulesSummaryOpen = false;
  },

  rulesExportDocument() {
    return wrapExportDocument(LEAGUE_RULES_EXPORT_KIND, {
      rules: leagueRulesExportPayload(this.form),
    });
  },

  exportRulesJson() {
    const doc = this.rulesExportDocument();
    const base = sanitizeExportBasename(this.form.displayName, 'league-rules');
    downloadBlob(`${base}.json`, JSON.stringify(doc, null, 2), 'application/json;charset=utf-8');
    this.$store.toastr.success('League rules exported as JSON');
  },

  exportRulesTxt() {
    const doc = this.rulesExportDocument();
    const summary = buildLeagueRulesSummaryLines(this.form);
    const text = textExportWithJsonSection(
      `League rules — ${this.form.displayName || 'Untitled'}`,
      summary,
      doc,
    );
    const base = sanitizeExportBasename(this.form.displayName, 'league-rules');
    downloadBlob(`${base}.txt`, text);
    this.$store.toastr.success('League rules exported as text');
  },

  async importRulesFile() {
    this.rulesImporting = true;
    try {
      const picked = await pickTextFile('.json,.txt,application/json,text/plain');
      if (!picked) return;
      const doc = parseImportDocument(picked.text, LEAGUE_RULES_EXPORT_KIND);
      const rules = doc.rules || doc;
      this.applyImportedRules(rules);
      this.$store.toastr.success('League rules imported — review and save when ready');
    } catch (e) {
      this.$store.toastr.error(e.message || 'Import failed');
    } finally {
      this.rulesImporting = false;
    }
  },

  applyImportedRules(rules) {
    if (!rules || typeof rules !== 'object') throw new Error('Invalid rules data');
    const defaultStartingScore = normalizeStartingScore(rules.defaultStartingScore, 501);
    const defaultMaxDartsPerLeg = normalizeMaxDarts(rules.defaultMaxDartsPerLeg);
    this.form.displayName = String(rules.displayName || this.form.displayName || '').trim();
    this.form.notes = String(rules.notes || '').trim();
    this.form.defaultCheckout = rules.defaultCheckout || this.form.defaultCheckout || 'double-out';
    this.form.defaultStartingScore =
      defaultStartingScore === 'cricket' ? 501 : Number(defaultStartingScore) || 501;
    this.form.defaultMaxDartsPerLeg = defaultMaxDartsPerLeg;
    this.form.matchStartTime = normalizeMatchStartTime(rules.matchStartTime || this.form.matchStartTime);
    this.form.fixtureCadence = rules.fixtureCadence || this.form.fixtureCadence || 'weekly';
    this.form.fixtureWeekday = rules.fixtureWeekday || this.form.fixtureWeekday || 'thu';
    this.form.fixtureWeekday2 = rules.fixtureWeekday2 || this.form.fixtureWeekday2 || 'tue';
    const games = Array.isArray(rules.gameTemplate) ? rules.gameTemplate : [];
    if (games.length) {
      this.form.gameTemplate = games.map((g) =>
        enrichGameTemplateRow(g, { defaultStartingScore, defaultMaxDartsPerLeg }),
      );
      this.renumberOrders();
    }
  },

  async confirmDelete() {
    if (!this.isEditing || !this.form._id) return;
    this.deleteOpen = true;
    this.usageLoading = true;
    try {
      const res = await apiFetch(`/league-rules/${encodeURIComponent(this.form._id)}`);
      const data = await res.json();
      if (res.ok) {
        this.linkedLeagues = Array.isArray(data?.linkedLeagues) ? data.linkedLeagues : [];
      }
    } catch {
      this.linkedLeagues = [];
    } finally {
      this.usageLoading = false;
    }
  },

  async doDelete() {
    if (!this.form._id || this.deleteBusy) return;
    this.deleteBusy = true;
    try {
      const res = await apiFetch(`/league-rules/${this.form._id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.message || 'Delete failed');
      this.$store.toastr.success('League rules deleted');
      this.deleteOpen = false;
      this.backToList();
    } catch (e) {
      this.$store.toastr.error(e.message || 'Delete failed');
    } finally {
      this.deleteBusy = false;
    }
  },

  async save() {
    if (this.saving) return;
    const displayName = String(this.form.displayName || '').trim();
    if (!displayName) {
      this.error = 'Enter a profile name.';
      this.$store.toastr.warning(this.error);
      return;
    }
    this.saving = true;
    this.error = '';
    try {
      const defaultStartingScore = normalizeStartingScore(this.form.defaultStartingScore, 501);
      const defaultMaxDartsPerLeg = normalizeMaxDarts(this.form.defaultMaxDartsPerLeg);
      const payload = {
        displayName,
        notes: String(this.form.notes || '').trim(),
        defaultCheckout: this.form.defaultCheckout,
        defaultStartingScore,
        defaultMaxDartsPerLeg,
        matchStartTime: normalizeMatchStartTime(this.form.matchStartTime),
        fixtureCadence: this.form.fixtureCadence || 'weekly',
        fixtureWeekday: this.form.fixtureWeekday || 'thu',
        fixtureWeekday2: this.form.fixtureWeekday2 || 'tue',
        gameTemplate: this.form.gameTemplate.map((g) => ({
          order: Number(g.order) || 1,
          type: g.type,
          format: resolveRowFormat(g, defaultStartingScore),
          bestOfLegs: clampBestOfLegs(g.bestOfLegs),
          label: String(g.label || '').trim().slice(0, 80),
          checkout: String(g.checkout || '').trim(),
          maxDartsPerLeg:
            g.maxDartsPerLeg != null && g.maxDartsPerLeg !== ''
              ? normalizeMaxDarts(g.maxDartsPerLeg)
              : defaultMaxDartsPerLeg,
        })),
      };

      let res;
      if (this.isEditing && this.form._id) {
        res = await apiFetch(`/league-rules/${this.form._id}`, { method: 'PATCH', body: payload });
      } else {
        res = await apiFetch('/league-rules', { method: 'POST', body: payload });
      }
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error(data?.message || 'Save failed');
      this.$store.toastr.success(this.isEditing ? 'League rules updated' : 'League rules created');
      if (this.isAccountSettingsEmbed()) {
        this.$store.accountLeagueRules.openList({ bump: true });
        return;
      }
      const router = globalThis.PineconeRouter;
      const next = '/admin/league-rules';
      if (router?.navigate) router.navigate(next);
      else globalThis.location.assign(next);
    } catch (e) {
      this.error = e.message || 'Save failed';
      this.$store.toastr.error(this.error, 4000);
    } finally {
      this.saving = false;
    }
  },
}));
